import React, { ElementType } from 'react';
import { Box, BoxProps } from '@mui/material';

export interface OverlayProps extends BoxProps<ElementType> {
  variant?: 'default' | 'hero';
  /** The content like image to be displayed inside the overlay.  */
  overlayContent?: React.ReactNode;
  /** custom props of the Box wrapper of the component. */
  wrapperProps?: BoxProps<ElementType>;
}

/**
 * The Overlay Component
 *
 * Overlay component wraps its children with an overlay effect. The overlay effect is achieved
 * using a semi-transparent gradient background. The component allows for custom content
 * (like an image) to be displayed inside the overlay through the `overlayContent` prop.
 */
export const Overlay: React.FC<OverlayProps> = ({ wrapperProps, sx, overlayContent, variant, children, ...rest }) => {
  const { sx: wrapperSx, ...restWrapper } = wrapperProps || {};

  return (
    <Box
      data-testid="Overlay-wrapper"
      sx={{ position: 'relative', maxWidth: '100%', maxHeight: '100%', ...wrapperSx }}
      {...restWrapper}
    >
      {overlayContent}
      <Box
        data-testid="Overlay-overlay"
        sx={{
          position: 'absolute',
          inset: '0',
          zIndex: '100',
          background:
            variant === 'hero'
              ? 'linear-gradient(0deg, rgba(35, 35, 35, 0.3), rgba(35, 35, 35, 0.3)), linear-gradient(180deg, rgba(0, 0, 0, 0.468) 0%, rgba(0, 0, 0, 0) 42.19%, rgba(28, 28, 28, 0.72) 100%)'
              : 'linear-gradient(0deg, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0))',
          ...sx,
        }}
        {...rest}
      >
        {children}
      </Box>
    </Box>
  );
};
